import "@fontsource-variable/figtree";
import { TranslationsPage } from "./app/menus/TranslationsPage";
import { BillingPage } from "./app/settings/BillingPage";
import { FeedbackPage } from "./app/stats/FeedbackPage";
import { StatsPage } from "./app/stats/StatsPage";
import "@fontsource-variable/bricolage-grotesque";
import "@fontsource-variable/fraunces/soft-italic.css";
import "./styles/tokens.css";
import "./styles/app.css";
import "./styles/kit.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router";
import { adminRoutes } from "./admin/AdminApp";
import { ApiRequestError } from "./api/client";
import { AppShell } from "./app/AppShell";
import { AppearancePage } from "./app/appearance/AppearancePage";
import { ImportPage } from "./app/import/ImportPage";
import { VerifyPage } from "./app/import/VerifyPage";
import { MenusPage } from "./app/menus/MenusPage";
import { ProductsPage } from "./app/menus/ProductsPage";
import { NAV_FLAT } from "./app/nav";
import { AllergenWizard } from "./app/nutrition/AllergenWizard";
import { NutritionPage } from "./app/nutrition/NutritionPage";
import { NutritionPrint } from "./app/nutrition/NutritionPrint";
import { NutritionSetup } from "./app/nutrition/NutritionSetup";
import { OverviewPage } from "./app/OverviewPage";
import { MorePage } from "./app/pages";
import { PublishedPage } from "./app/qr/PublishedPage";
import { QrPage } from "./app/qr/QrPage";
import { SettingsPage } from "./app/settings/SettingsPage";
import { StartWizard } from "./app/start/StartWizard";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastProvider } from "./components/Toast";
import { Alert, AuthLayout } from "./components/ui";
import { LegalDraftPage } from "./pages/LegalDraftPage";
import { LoginPage } from "./pages/LoginPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";

function Loading() {
  return (
    <div className="center-page" role="status">
      <span className="muted">Se încarcă…</span>
    </div>
  );
}

function LoadError({ message }: { message: string }) {
  return (
    <AuthLayout>
      <Alert tone="error" title="Nu am putut încărca contul." live>
        {message}
      </Alert>
      <button type="button" className="btn btn-secondary" onClick={() => window.location.reload()}>
        Reîncearcă
      </button>
    </AuthLayout>
  );
}

/** Authenticated area: anonymous → login; no restaurant yet → onboarding; otherwise the dashboard shell. */
function ProtectedApp() {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === "loading") return <Loading />;
  if (state.status === "error") return <LoadError message={state.message} />;
  if (state.status === "anonymous") {
    // The page's filters (?menu=…, ?q=…) come back after signing in too.
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  const membership = state.me.memberships[0];
  if (!membership) return <OnboardingPage />;
  return <AppShell me={state.me} membership={membership} />;
}

/** Login/registration pages redirect away when a session already exists. */
function GuestOnly({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") return <Loading />;
  if (state.status === "authenticated") return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <AuthLayout>
      <h1 className="h1">Pagina nu există</h1>
      <p className="muted" style={{ margin: 0 }}>
        Verifică adresa sau mergi la <a href="/app">prezentarea generală</a>.
      </p>
    </AuthLayout>
  );
}

const LIVE: Record<string, React.ReactNode> = {
  appearance: <AppearancePage />,
  menus: <MenusPage />,
  products: <ProductsPage />,
  settings: <SettingsPage />,
  qr: <QrPage />,
  import: <ImportPage />,
  billing: <BillingPage />,
  translations: <TranslationsPage />,
  stats: <StatsPage />,
  feedback: <FeedbackPage />,
  nutrition: <NutritionPage />,
};

const sectionRoutes = NAV_FLAT.filter((i) => i.id !== "overview").map((i) => ({
  path: i.path.replace("/app/", ""),
  element: LIVE[i.id] ?? <Navigate to="/app" replace />,
}));

// admin.meniova.com shows only the super admin (its own sign-in); client.meniova.com never shows it. Locally both
// live on one origin: the app, and the super admin under /admin.
const host = window.location.hostname;
const ADMIN_HOST = host.startsWith("admin.");
const LOCAL = host === "localhost" || host === "127.0.0.1";

const adminOnly = [
  { path: "/", element: <Navigate to="/admin" replace /> },
  ...adminRoutes,
  { path: "/login", element: <Navigate to="/admin/login" replace /> },
  { path: "/resetare-parola", element: <ResetPasswordPage /> },
  { path: "*", element: <NotFound /> },
];

const router = createBrowserRouter(
  ADMIN_HOST
    ? adminOnly
    : [
        ...(LOCAL ? adminRoutes : []),
        { path: "/", element: <Navigate to="/app" replace /> },
        {
          path: "/login",
          element: (
            <GuestOnly>
              <LoginPage />
            </GuestOnly>
          ),
        },
        {
          path: "/inregistrare",
          element: (
            <GuestOnly>
              <RegisterPage />
            </GuestOnly>
          ),
        },
        { path: "/verificare-email", element: <VerifyEmailPage /> },
        { path: "/resetare-parola", element: <ResetPasswordPage /> },
        { path: "/termeni", element: <LegalDraftPage title="Termeni de utilizare" doc="terms" /> },
        {
          path: "/confidentialitate",
          element: <LegalDraftPage title="Nota de confidențialitate" doc="privacy" />,
        },
        {
          path: "/app",
          element: <ProtectedApp />,
          children: [
            { index: true, element: <OverviewPage /> },
            ...sectionRoutes,
            { path: "more", element: <MorePage /> },
            // Link of the support e-mails: opens the chatbox (bottom right).
            { path: "suport", element: <Navigate to="/app?suport=1" replace /> },
            { path: "nutrition/alergeni", element: <AllergenWizard /> },
            { path: "nutrition/configurare", element: <NutritionSetup /> },
            { path: "start", element: <StartWizard /> },
            { path: "nutrition/tiparire", element: <NutritionPrint /> },
            { path: "publicat", element: <PublishedPage /> },
            { path: "verifica/:jobId", element: <VerifyPage /> },
          ],
        },
        { path: "*", element: <NotFound /> },
      ],
);

// Server state: retry only transient failures; never retry 4xx (validation, auth, tenant guard).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: (count, err) =>
        count < 2 && (!(err instanceof ApiRequestError) || err.status === 0 || err.status >= 500),
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");
createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
